import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
  KeyboardEvent as ReactKeyboardEvent,
  useMemo,
} from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Command, CommandGroup, CommandItem, CommandList } from './Command';
import { cn } from './lib/utils';
import Icon from '@/Components/UI/Icon/Icon';
import PopoverV2 from '../Popover/PopoverV2';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import { getTypeIconPath } from '@/Utils/getTypeIconPath';
import { FILTER_SELECT_ALL_LIMIT } from '@/Utils/constants';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { Button } from '../Button/Button';

const COMMAND_ITEM_CLASSNAMES =
  'relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none cursor-pointer gap-2 truncate';

const PADDING = 40;

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
  fixed?: boolean;
  [key: string]: string | boolean | undefined;
}

interface MultipleSelectorProps {
  value?: Option[];
  options: Option[];
  placeholder?: string;
  emptyIndicator?: React.ReactNode;
  onChange?: (options: Option[], name?: string) => void;
  onInputChange?: (inputValue: string) => void;
  maxSelected?: number;
  onMaxSelected?: (maxLimit: number) => void;
  name?: string;
  disabled?: boolean;
  groupBy?: string;
  className?: string;
  selectFirstItem?: boolean;
  commandProps?: React.ComponentPropsWithoutRef<typeof Command>;
  inputProps?: Omit<
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
    'value' | 'placeholder' | 'disabled'
  >;
  selectionMode?: 'single' | 'multiple';
  dataCy?: string;
  dataTestId?: string;
  isOpenbyDefault?: boolean;
  freeSolo?: boolean;
  renderSelectedValues?: (selected: Option[]) => React.ReactNode;
  validateInput?: (inputValue: string) => string | null;
  showLimitedValueWarning?: boolean;
  placeholderClassName?: string;
}

export interface MultipleSelectorRef {
  selectedValue: Option[];
  input: HTMLInputElement;
}

const useFilteredOptions = (
  arrayOptions: Option[],
  debouncedInputValue: string,
  freeSolo: boolean,
  selectionMode: 'single' | 'multiple'
) => {
  return useMemo(() => {
    if (!debouncedInputValue && (!freeSolo || selectionMode !== 'multiple')) {
      return arrayOptions;
    }

    const debouncedInputValueHasCommas = debouncedInputValue.includes(',');

    if (debouncedInputValueHasCommas) {
      const commaSeparatedinputValues = debouncedInputValueHasCommas
        ? Array.from(
            new Set(
              debouncedInputValue
                .split(',')
                .map((val) => val.trim())
                .filter(Boolean)
            )
          )
        : [];

      return commaSeparatedinputValues.map((inputVal) => ({
        value: inputVal,
        label: inputVal,
      }));
    }

    let newOptions: Option[] = [];

    if (freeSolo && debouncedInputValue) {
      newOptions = [{ value: debouncedInputValue, label: debouncedInputValue }];
    }

    const matchingExistingOptions = arrayOptions.filter((option) => {
      return option.label.toLowerCase() === debouncedInputValue.toLowerCase();
    });

    // Escape a string so it can be safely inserted into a RegExp constructor.
    const escapeRegExp = (str: string) => {
      return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    };

    const otherFilteredOptions = arrayOptions
      .filter((option) => {
        const safeValue = escapeRegExp(debouncedInputValue);
        const regex = new RegExp(safeValue, 'i');
        return regex.test(option.label);
      })
      .filter((option) => !matchingExistingOptions.includes(option));

    newOptions = newOptions.filter((newOption) => {
      const isDuplicate = arrayOptions.some(
        (existingOption) => existingOption.label === newOption.label
      );
      return !isDuplicate;
    });

    let finalOptions = [];

    finalOptions = [...newOptions];

    if (matchingExistingOptions.length > 0) {
      finalOptions = [...finalOptions, ...matchingExistingOptions];
    }

    finalOptions = [...finalOptions, ...otherFilteredOptions];

    return finalOptions;
  }, [debouncedInputValue, freeSolo, arrayOptions]);
};

export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedValue !== value) {
        setDebouncedValue(value);
      }
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay, debouncedValue]);

  return debouncedValue;
}

function calculateMaxWidthFromTopSorted(
  options: any[],
  maxSortSize = 100,
  averageCharWidth = 7,
  minAllowedWidth = 250,
  maxAllowedWidth = 750
) {
  const sortedOptions = options
    .slice(0, maxSortSize)
    .sort((a, b) => b.label.length - a.label.length);

  const maxLabelLength = sortedOptions.length > 0 ? sortedOptions[0].label.length : 0;
  const estimatedWidth = maxLabelLength * averageCharWidth + PADDING;

  return Math.max(minAllowedWidth, Math.min(estimatedWidth, maxAllowedWidth));
}

function groupOptionsByProperty(
  options: Option[],
  propertyKey?: string
): { key: string; options: Option[] }[] {
  if (options.length === 0) {
    return [];
  }

  if (!propertyKey) {
    return [{ key: '', options }];
  }

  const groupedOptions = new Map<string, Option[]>();
  options.forEach((option) => {
    const key = (option[propertyKey] as string) || '';
    if (!groupedOptions.has(key)) {
      groupedOptions.set(key, []);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    groupedOptions.get(key)!.push(option);
  });

  return Array.from(groupedOptions.entries()).map(([key, options]) => ({ key, options }));
}

const ComboBox = forwardRef<MultipleSelectorRef, MultipleSelectorProps>(
  (
    {
      value = [],
      onChange,
      onInputChange,
      placeholder = 'Search or type',
      options: arrayOptions,
      emptyIndicator,
      maxSelected = Number.MAX_SAFE_INTEGER,
      onMaxSelected,
      name,
      disabled,
      groupBy,
      className,
      selectFirstItem = true,
      commandProps,
      inputProps,
      selectionMode = 'single',
      dataCy,
      dataTestId,
      isOpenbyDefault,
      freeSolo = false,
      renderSelectedValues,
      validateInput,
      showLimitedValueWarning,
      placeholderClassName = '',
    }: MultipleSelectorProps,
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const [isPopoverOpen, setIsPopoverOpen] = useState(isOpenbyDefault || false);
    const [selected, setSelected] = useState<Option[]>(value[0] ? value : []);
    const [options, setOptions] = useState<Option[]>(arrayOptions);
    const [inputValue, setInputValue] = useState('');
    const [selectedValuesString, setSelectedValuesString] = useState('');
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
    const [inputValidationMessage, setInputValidationMessage] = useState<string | null>(null);
    const [inputContainsComma, setInputContainsComma] = useState(false);
    const [isSelectAllAllowed, setIsSelectAllAllowed] = useState(false);
    const [listWidth, setListWidth] = useState(0);

    const debouncedInputValue = useDebounce(inputValue, 300);

    const handleValidation = (value: string) => {
      if (validateInput) {
        const validationResult = validateInput(value);
        if (validationResult !== null) {
          // Input is invalid
          setInputValidationMessage(validationResult);
          return false;
        }
      }
      // Input is valid
      setInputValidationMessage(null);
      return true;
    };

    const handleClear = () => {
      setSelected([]);
      setInputValue('');
      onChange?.([], name);
    };

    const handleSelect = useCallback(
      (option: Option) => {
        if (!handleValidation(option.value)) return;

        if (selectionMode === 'multiple') {
          const isAlreadySelected = selected.some(
            (selectedOption) => selectedOption.value === option.value
          );
          if (isAlreadySelected) {
            // Option is already selected; do nothing
            return;
          }
          if (selected.length >= maxSelected) {
            onMaxSelected?.(maxSelected);
          } else {
            const newOptions = [...selected, option];
            setSelected(newOptions);
            onChange?.(newOptions, name);
          }
        } else {
          setSelected([option]);
          onChange?.([option], name);
          setTimeout(() => setIsPopoverOpen(false), 0);
        }
      },
      [selected, handleValidation, maxSelected, selectionMode, onChange, name]
    );

    const handleUnselect = useCallback(
      (option: Option) => {
        const newOptions = selected.filter((s) => s.value !== option.value);
        setSelected(newOptions);
        onChange?.(newOptions, name);
      },
      [selected, onChange, name]
    );

    const handleSelectAll = () => {
      const newSelected = isSelectAllChecked ? [] : [...selected, ...filteredOptions];
      setSelected(newSelected);
      onChange?.(newSelected, name);
      setIsSelectAllChecked(!isSelectAllChecked);
    };

    const handleSelectMany = useCallback(
      (optionsToSelect: Option[]) => {
        const newOptions = [...selected, ...optionsToSelect];
        setSelected(newOptions);
        onChange?.(newOptions, name);
      },
      [selected, onChange, name]
    );

    const handleUnselectMany = useCallback(
      (optionsToUnselect: Option[]) => {
        const newSelected = selected.filter(
          (selectedOption) =>
            !optionsToUnselect.some((option) => option.value === selectedOption.value)
        );
        setSelected(newSelected);
        onChange?.(newSelected, name);
      },
      [selected, onChange, name]
    );

    const filteredOptions = useFilteredOptions(
      arrayOptions,
      debouncedInputValue,
      freeSolo,
      selectionMode
    );

    const parentRef = useRef<HTMLDivElement>(null);
    const rowVirtualizer = useVirtualizer({
      count: filteredOptions.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 35,
    });

    const [groupedOptions, setGroupedOptions] = useState<{ key: string; options: Option[] }[]>([]);

    const handleKeyDown = useCallback(
      (e: ReactKeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Escape' || e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
          if (e.key === 'Escape') {
            input.blur();
          }
          if (e.key === 'Enter') {
            if (freeSolo) {
              const debouncedInputValueHasCommas = debouncedInputValue.includes(',');

              if (debouncedInputValueHasCommas) {
                const commaSeparatedInputValues = Array.from(
                  new Set(
                    debouncedInputValue
                      .split(',')
                      .map((val) => val.trim())
                      .filter(Boolean)
                  )
                );

                // Validate all input values
                const invalidInputs = commaSeparatedInputValues.filter(
                  (inputVal) => !handleValidation(inputVal)
                );

                if (invalidInputs.length > 0) {
                  // If any input fails validation, do not proceed
                  setInputValidationMessage('One or more inputs are invalid.');
                  return;
                } else {
                  // All inputs are valid; proceed
                  const commaSeparatedOptions = commaSeparatedInputValues.map((inputVal) => ({
                    value: inputVal,
                    label: inputVal,
                  }));

                  // Split options into those to select and those to unselect
                  const optionsToSelect = commaSeparatedOptions.filter(
                    (option) =>
                      !selected.some((selectedOption) => selectedOption.value === option.value)
                  );

                  const optionsToUnselect = commaSeparatedOptions.filter((option) =>
                    selected.some((selectedOption) => selectedOption.value === option.value)
                  );

                  if (optionsToUnselect.length > 0) {
                    handleUnselectMany(optionsToUnselect);
                  }

                  if (optionsToSelect.length > 0) {
                    handleSelectMany(optionsToSelect);
                  }
                }
              } else if (input.value.trim()) {
                const inputVal = input.value.trim();

                // Validate the input
                if (!handleValidation(inputVal)) {
                  // Input is invalid, set validation message and do not proceed
                  setInputValidationMessage('Invalid input.');
                  return;
                }

                const newOption: Option = {
                  value: inputVal,
                  label: inputVal,
                };

                const isAlreadySelected = selected.some(
                  (option) => option.value === newOption.value
                );

                if (isAlreadySelected) {
                  handleUnselect(newOption);
                } else {
                  handleSelect(newOption);
                }
              }
            } else {
              const activeElement = document.activeElement as HTMLElement;

              const activeOption = options.find(
                (option) => option.label === activeElement?.innerText.trim()
              );

              if (activeOption) {
                // Validate the active option before selecting
                if (!handleValidation(activeOption.value)) {
                  setInputValidationMessage('Invalid input.');
                  return;
                }

                const isAlreadySelected = selected.some(
                  (option) => option.value === activeOption.value
                );
                if (isAlreadySelected) {
                  handleUnselect(activeOption);
                } else {
                  handleSelect(activeOption);
                }
              }
            }
          }
        }
      },
      [
        handleSelect,
        handleUnselect,
        handleSelectMany,
        handleUnselectMany,
        freeSolo,
        options,
        handleValidation,
        selected,
        debouncedInputValue,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        selectedValue: [...selected],
        input: inputRef.current as HTMLInputElement,
      }),
      [selected]
    );

    useEffect(() => {
      setInputContainsComma(debouncedInputValue.includes(','));
      onInputChange?.(debouncedInputValue);
    }, [debouncedInputValue]);

    useEffect(() => {
      if (options.length > 100) {
        const calculatedWidth = calculateMaxWidthFromTopSorted(options);
        setListWidth(calculatedWidth);
      }
    }, [options]);

    useEffect(() => {
      setIsSelectAllAllowed(filteredOptions?.length <= FILTER_SELECT_ALL_LIMIT);
    }, [filteredOptions]);

    useEffect(() => {
      const filteredWithoutDuplicates = filteredOptions.filter(
        (option) => !selected.some((selectedOption) => selectedOption.value === option.value)
      );
      const [firstItem] = filteredWithoutDuplicates;
      const restOfFilteredOptions = filteredWithoutDuplicates.slice(1);
      let optionsToGroup;

      const isFirstItemInSelectedOptions = selected.some(
        (option) => option.label === firstItem?.label
      );

      if (selectionMode === 'single') {
        optionsToGroup = filteredOptions;
      } else if (debouncedInputValue) {
        if (debouncedInputValue.includes(',')) {
          optionsToGroup = [...selected, ...filteredWithoutDuplicates];
        } else if (
          firstItem?.label &&
          firstItem?.label === debouncedInputValue &&
          !isFirstItemInSelectedOptions
        ) {
          optionsToGroup = [firstItem, ...selected, ...restOfFilteredOptions];
        } else {
          optionsToGroup = [...selected, ...filteredWithoutDuplicates];
        }
      } else {
        optionsToGroup = [...selected, ...filteredWithoutDuplicates];
      }

      setGroupedOptions(groupOptionsByProperty(optionsToGroup, groupBy));
    }, [filteredOptions, groupBy, isPopoverOpen, debouncedInputValue, selected, selectionMode]);

    useEffect(() => {
      if (value?.length && value[0]) {
        setSelected(value);
      } else {
        setSelected([]);
      }
    }, [value, freeSolo, options]);

    useEffect(() => {
      if (JSON.stringify(arrayOptions) !== JSON.stringify(options)) {
        setOptions(arrayOptions);
      }
    }, [arrayOptions, options]);

    useEffect(() => {
      const divElement = divRef.current;

      const handleDivKeyDown = (event: KeyboardEvent) => {
        if (['Enter', 'ArrowDown', 'Space'].includes(event.key)) {
          setIsPopoverOpen(true);
          inputRef.current?.focus();
        }
      };

      divElement?.addEventListener('keydown', handleDivKeyDown);

      return () => {
        divElement?.removeEventListener('keydown', handleDivKeyDown);
      };
    }, []);

    useEffect(() => {
      if (!isPopoverOpen) {
        setInputValue('');
        setInputValidationMessage(null);
      }
    }, [isPopoverOpen]);

    useEffect(() => {
      handleValidation(inputValue);
    }, [inputValue]);

    useEffect(() => {
      if (!isPopoverOpen) return;

      const timeoutId = setTimeout(() => {
        const commandListElement = listRef.current;
        if (!commandListElement) return;

        const handleArrowKeys = (event: KeyboardEvent) => {
          const items = Array.from(
            commandListElement.querySelectorAll('[cmdk-item]')
          ) as HTMLElement[];
          const selectedIndex = items.findIndex(
            (item) => item.getAttribute('aria-selected') === 'true'
          );
          let nextIndex: number;

          if (event.key === 'ArrowDown') {
            nextIndex = (selectedIndex + 1) % items.length;
          } else if (event.key === 'ArrowUp') {
            nextIndex = (selectedIndex - 1 + items.length) % items.length;
          } else {
            return;
          }

          items.forEach((item, index) => {
            item.tabIndex = index === nextIndex ? 0 : -1;
            item.setAttribute('aria-selected', index === nextIndex ? 'true' : 'false');
          });

          items[nextIndex].focus();
        };

        commandListElement.addEventListener('keydown', handleArrowKeys);

        return () => {
          clearTimeout(timeoutId);
          commandListElement.removeEventListener('keydown', handleArrowKeys);
        };
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [isPopoverOpen]);

    const getSelectedLabels = () =>
      renderSelectedValues
        ? renderSelectedValues(selected)
        : selected.map((option) => option.label).join(', ');

    useEffect(() => {
      if (Array.isArray(selected) && selected.length > 0) {
        setSelectedValuesString(selected?.map((option) => option.value).join(','));
      }
    }, [selected]);

    useEffect(() => {
      setIsSelectAllChecked(
        filteredOptions.length > 0 &&
          selected.length > 0 &&
          filteredOptions.every((filteredOption) =>
            selected.some((selectedOption) => filteredOption.value === selectedOption.value)
          )
      );
    }, [selected, filteredOptions]);

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsPopoverOpen(true);
      inputProps?.onFocus?.(event);
    };

    const renderOptionItem = (option: Option, inputContainsComma: boolean, index: number) => {
      if (!option?.label) return null;
      const isOptionSelected = selected.some(
        (selectedOption) => selectedOption.value === option.value
      );

      const handleOptionClick = () => {
        if (isOptionSelected && selectionMode === 'multiple') {
          handleUnselect(option);
        } else {
          handleSelect(option);
        }
      };

      const OptionContent = (
        <>
          {selectionMode === 'multiple' && (
            <input type='checkbox' checked={isOptionSelected} readOnly />
          )}
          {option.icon && <Icon name={option.icon} className='flex-shrink-0 mr-1' />}
          {option.type && typeof option.type === 'string' && (
            <Icon
              name={getTypeIconPath(option.type)}
              className='flex-shrink-0 mr-1 combo-option-type'
              dataCy={option.type}
            />
          )}
          {option.label}
        </>
      );

      const commonProps = {
        onMouseDown: (e: React.MouseEvent) => {
          handleOptionClick();
          e.preventDefault();
          e.stopPropagation();
        },
        className: cn(
          'cursor-pointer gap-2 truncate',
          COMMAND_ITEM_CLASSNAMES,
          'hover:bg-gray-100',
          inputContainsComma ? 'focus:bg-midnight-100 aria-selected:bg-midnight-100' : '',
          option.disabled && 'cursor-default text-ui'
        ),
      };

      return (
        <div
          key={`${option.value}-${index}`}
          {...commonProps}
          id={option.label}
          data-cy={`select-option-${index + 1}`}
          data-testid={`select-option-${index + 1}`}
        >
          {OptionContent}
        </div>
      );
    };

    const renderOptionsList = (options: Option[]) => {
      if (options.length > 100) {
        return (
          <div ref={parentRef} style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: listWidth,
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem, index) => {
                const option = groupedOptions[0].options[virtualItem.index];
                return (
                  <div
                    key={virtualItem.key.toString()}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {renderOptionItem(option, inputContainsComma, index)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      } else {
        return (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <div>
              {options.map((option, index) => renderOptionItem(option, inputContainsComma, index))}
            </div>
          </div>
        );
      }
    };

    const SelectAllInputAndLabel = () => (
      <div
        className={clsx(
          'flex items-center gap-2',
          (!isSelectAllAllowed || inputValidationMessage) && 'opacity-40 cursor-default'
        )}
      >
        <input className='' type='checkbox' checked={isSelectAllChecked} readOnly />
        <span>Select All</span>
      </div>
    );

    return (
      <>
        <input type='hidden' name={name} value={selectedValuesString} />
        <Command
          {...commandProps}
          shouldFilter={false}
          onKeyDown={(e: ReactKeyboardEvent<HTMLDivElement>) => {
            handleKeyDown(e);
            commandProps?.onKeyDown?.(e);
          }}
          className={cn(
            'overflow-visible  !cursor-pointer',
            commandProps?.className,
            disabled ? 'bg-ui-secondary placeholder-ui-secondary' : 'bg-white placeholder-ui'
          )}
          data-cy={dataCy}
          data-testid={dataTestId}
        >
          <PopoverV2
            align='start'
            ref={listRef}
            content={
              <div
                className='relative overflow-hidden z-100'
                data-cy='combobox'
                data-testid='combobox'
              >
                <CommandPrimitive.Input
                  {...inputProps}
                  ref={inputRef}
                  value={inputValue}
                  disabled={disabled}
                  onValueChange={(value) => {
                    setInputValue(value);
                    inputProps?.onValueChange?.(value);
                  }}
                  onBlur={inputProps?.onBlur}
                  onFocus={handleFocus}
                  placeholder={placeholder}
                  className={cn(
                    'ml-2 mt-3 mb-1 flex-1 bg-white z-10 outline-none placeholder:text-ui-helper border rounded px-2 w-[93%] h-8 text-sm',
                    inputProps?.className,
                    inputValidationMessage && inputValue?.length !== 0 && 'border-red-500'
                  )}
                  autoFocus
                  data-cy={dataCy ? `${dataCy}-value` : 'combobox-input'}
                />
                {inputValidationMessage && inputValue?.length !== 0 && (
                  <div className='h-4 px-1 mx-2 mb-2 text-xs font-semibold text-red-500 bg-red-100 rounded bg-opacity-40 w-fit'>
                    {inputValidationMessage}
                  </div>
                )}
                {selectionMode === 'multiple' && (
                  <div className='sticky top-0 z-10 flex flex-row items-center justify-between bg-white border border-t-0 border-x-0 border-b-ui'>
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        isSelectAllAllowed && !inputValidationMessage && handleSelectAll();
                      }}
                      className={cn('ml-1', COMMAND_ITEM_CLASSNAMES)}
                    >
                      {isSelectAllAllowed ? (
                        <SelectAllInputAndLabel />
                      ) : (
                        <>
                          <Tooltip
                            text={
                              'You can only select all when the number of values is less than 1,000.'
                            }
                            side='top'
                          >
                            <SelectAllInputAndLabel />
                          </Tooltip>
                        </>
                      )}
                    </div>
                    <Button
                      variant='ghost'
                      disabled={!selected.length && !inputValue.length}
                      size='small'
                      onClick={handleClear}
                      className={clsx(
                        'mx-1 !text-sm',
                        !selected.length &&
                          'hover:bg-transparent focus:bg-transparent active:bg-transparent'
                      )}
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <CommandList className='overflow-hidden'>
                  <>
                    {!groupedOptions.length && emptyIndicator}
                    {!selectFirstItem && <CommandItem value='-' className='hidden' />}

                    {!inputContainsComma
                      ? groupedOptions.map(({ key, options }) => (
                          <CommandGroup
                            key={key}
                            heading={key}
                            className='h-full bg-white !overflow-visible'
                            data-cy={`${dataCy}-dropdown`}
                          >
                            {renderOptionsList(options)}
                          </CommandGroup>
                        ))
                      : groupedOptions.map(({ key, options }) => (
                          <div
                            key={key}
                            className='h-full bg-white !overflow-visible p-1'
                            data-cy={`${dataCy}-dropdown`}
                          >
                            {renderOptionsList(options)}
                          </div>
                        ))}
                  </>
                </CommandList>
                {showLimitedValueWarning && !isSelectAllAllowed && (
                  <div className='flex items-center justify-center w-full h-8 gap-2 py-1 border-t cursor-default'>
                    <Tooltip
                      side='bottom'
                      text='Only the first 10,000 unique values are displayed. This column exceeds 10,000 unique values.'
                    >
                      <div className='flex items-center w-full gap-2'>
                        <Icon name='warning' color='#B39500' size={16} />
                        <p className='pt-[2px] typography-sm text-sunrise-800 !font-semibold'>
                          Not all values showing
                        </p>
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            }
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
          >
            <div
              ref={divRef}
              className={cn('group rounded border border-ui-200 text-sm', className)}
              tabIndex={0}
              data-testid={`${dataTestId}-trigger`}
            >
              <div className='relative flex flex-row items-center justify-start h-8 pr-3 mx-2 truncate'>
                {Array.isArray(selected) && selected.length > 0 ? (
                  selected.length === 1 ? (
                    <div className='flex-1 gap-2 truncate' data-cy='combobox-selected'>
                      {selected[0]?.type && (
                        <Icon
                          name={getTypeIconPath(selected[0].type)}
                          className='flex-shrink-0 inline-block mr-1 combo-option-type'
                        />
                      )}
                      {selected[0]?.label}
                    </div>
                  ) : (
                    <div className='flex-1 truncate' data-cy='combobox-selected'>
                      {getSelectedLabels()}
                    </div>
                  )
                ) : (
                  <span
                    data-cy='combobox-placeholder'
                    className={clsx('typography-body !text-ui-helper', placeholderClassName)}
                  >
                    {placeholder}
                  </span>
                )}
                <Icon
                  className='absolute right-0 ml-2'
                  name={isPopoverOpen ? 'caret-up' : 'caret-down-regular'}
                  size={12}
                  color='#151515'
                />
              </div>
            </div>
          </PopoverV2>
        </Command>
      </>
    );
  }
);

ComboBox.displayName = 'ComboBox';
export default ComboBox;
